import * as Errors from './errors';
import { Query } from './Query';
import { buildRequestHeaders, buildRequestConfig } from './utils';
import { Http } from './Http';

/**
 * SDKConfig
 * @typedef {Object} SDKConfig
 * @property {string} [host='http://localhost']  host сервера
 * @property {?string} [connection=null] строка с описанием соединения
 * @property {boolean} [replaceFilterValues=true]   нужно ли заменять константы в значениях фильтров
 * @property {string} [authToken=null]      auth token
 * @property {boolean} [admin=false]        admin mode
 * @property {function(SDK): Promise<void>} [beforeRequest=null]
 * @property {function(error: Error): Error} [processResponseError=null]
 * @property {function(result: DremioResult): DremioResult} [processResponseResult=null]
 */
/**
 * IQuerySchema
 * @typedef {Object} IQuerySchema
 * @property {Array} $from
 * @property {Object} $fields
 * @property {Array} $metrics
 * @property {Array} $dimensions
 * @property {Array} $filters
 * @property {Array} $sort
 */
/**
 * DremioResult
 * @typedef {Object} DremioResult
 * @property {Number} rowCount    кол-во записей
 * @property {Array} schema       схема данных
 * @property {Array} rows         данные
 */
/**
 * DremioRootEntity
 * @typedef {Object} DremioRootEntity
 * @property {String} id
 * @property {Array} path
 * @property {String} type
 * @property {String} containerType
 */
/**
 * DremioContainerInfo
 * @typedef {Object} DremioContainerInfo
 * @property {String} id
 * @property {Array} path
 * @property {String} name
 * @property {String} entityType
 * @property {DremioContainerChildInfo[]} children
 */
/**
 * DremioContainerChildInfo
 * @typedef {Object} DremioContainerChildInfo
 * @property {String} id
 * @property {Array} path
 * @property {String} type
 * @property {String} datasetType
 * @property {String} containerType
 */
/**
 * DremioEntityInfo
 * @typedef {Object} DremioEntityInfo
 * @property {String} id
 * @property {Array} path
 * @property {String} type
 * @property {String} entityType
 * @property {Array} fields
 */
/**
 * @typedef {Object} DremioPermissions
 * @property {String} employeeId
 * @property {Array<Object>} permissions
 */
/**
 * @typedef {Object} DremioPermissionInfo
 * @property {String} name
 */
/**
 * @typedef {Promise} PromiseCancelable
 * @property {Function} cancel
 */

/**
 * @param {string} connection
 * @param {string} host
 * @return {{PERMISSIONS: {method: string, options: {baseURL}, params: {connection}, url: string}, CATALOG: {method: string, options: {baseURL}, params: {connection}, url: string}, DATA: {method: string, data: {connection}, options: {baseURL}, url: string}, PERMISSIONS_INFO: {method: string, options: {baseURL}, params: {connection}, url: string}, DREMIO_URL: {options: {baseURL}, params: {connection}, url: string}, CATALOG_BY_PATH: {method: string, data: {connection}, options: {baseURL}, url: string}}}
 * @constructor
 */
const DataApiActions = ({ connection, host: baseURL }) => ({
    DREMIO_URL: {
        url: 'v1/dremio-url',
        params: {
            connection
        },
        options: {
            baseURL,
            method: 'get'
        }
    },
    PERMISSIONS: {
        url: 'v1/permissions',
        params: {
            connection
        },
        options: {
            baseURL,
            method: 'get'
        }
    },
    PERMISSIONS_INFO: {
        url: 'v1/permissions-info',
        params: {
            connection
        },
        options: {
            baseURL,
            method: 'get'
        }
    },
    CATALOG: {
        url: 'v1/catalog',
        params: {
            connection
        },
        options: {
            baseURL,
            method: 'get'
        }
    },
    CATALOG_BY_PATH: {
        url: 'v1/catalog/by-path',
        data: {
            connection
        },
        options: {
            baseURL,
            method: 'post'
        }
    },
    DATA: {
        url: 'v1/data',
        data: {
            connection
        },
        options: {
            baseURL,
            method: 'post'
        }
    }
});

/**
 *
 * @type {{replaceFilterValues: boolean, authToken: null, processResponseError: null, host: string, admin: boolean, connection: null, beforeRequest: null}}
 */
const ConfigDefaults = {
    host: 'http://localhost',
    connection: null,
    replaceFilterValues: true,
    authToken: null,
    admin: false,
    beforeRequest: null,
    processResponseError: null,
    processResponseResult: null
};

/**
 * @class
 */
class SDK {
    /**
     * Constructor
     * @param {SDKConfig} [config] config
     */
    constructor(config) {
        /**
         * @property {SDKConfig}
         */
        this.config = { ...ConfigDefaults, ...config };

        const {
            beforeRequest,
            processResponseError
        } = this.config;

        this.transport = new Http({
            beforeRequest,
            processResponseError,
            getHeaders: () => buildRequestHeaders(this.config),
            getBaseURL: () => this.config.host
        });

        this.processResponseResult = this.config.processResponseResult;
    }

    /**
     * Возвращает ссылку на dremio инстанс
     * @deprecated
     * @return {PromiseCancelable.<string, Error>}
     */
    getDremioUrl() {
        return this.requestAction({ action: DataApiActions(this.config).DREMIO_URL });
    }

    /**
     * Возвращает список прав текущего пользователя (если задан authToken {@link SDK#config} и он валиден)
     * @return {PromiseCancelable.<DremioPermissions, Error>}
     */
    getPermissions() {
        return this.requestAction({ action: DataApiActions(this.config).PERMISSIONS });
    }

    /**
     * Возвращает информацию о правах
     * @return {PromiseCancelable.<DremioPermissionInfo[], Error>}
     */
    getPermissionsInfo() {
        return this.requestAction({ action: DataApiActions(this.config).PERMISSIONS_INFO });
    }

    /**
     * Возрвщает root сущности dremio
     * @return {PromiseCancelable.<DremioRootEntity[], Error>}
     */
    getRootEntities() {
        return this.requestAction({ action: DataApiActions(this.config).CATALOG });
    }

    /**
     * Возвращает сущность dremio.CatalogEntity по path
     * @param {Array} [path=null]    путь
     * @return {PromiseCancelable.<DremioContainerInfo | DremioEntityInfo, Error>}
     */
    getEntityByPath(path) {
        const params = { path };
        return this.requestAction({ action: DataApiActions(this.config).CATALOG_BY_PATH, params });
    }

    /**
     * Выполняет запрос
     * @param {IQuerySchema} query      query
     * @param {Number} [offset=0]       offset
     * @param {Number} [limit=10]       limit
     * @param {Boolean} [debug=false]   debug
     * @return {PromiseCancelable.<DremioResult, Error>}
     */
    getData(query, offset = 0, limit = 10, debug = false) {
        if (this.config.replaceFilterValues) {
            query = Query.queryReplaceAllFilterValues(Query.clone(query));
        }
        const params = {
            query,
            limit,
            offset
        };
        if (debug) {
            params.debug = debug;
        }

        return this.requestAction({ action: DataApiActions(this.config).DATA, params })
            .then((result) => {
                if (result == null) {
                    return result;
                }
                if (this.processResponseResult == null) {
                    return result;
                }
                return this.processResponseResult(result);
            });
    }

    cancelActiveRequests() {
       this.transport.dispose();
    }

    /**
     *
     * @param {{ url: string, options?: { method?: string } }} action
     * @param {Record<string, string|number>} [pathParams]
     * @param {Record<string, any>} [params]
     * @param {Record<string, string|number|boolean|null>} [queryParams]
     * @param {Record<string, any>} [options]
     * @return {PromiseCancelable}
     */
    requestAction({ action, pathParams = {}, params = {}, queryParams, options }) {
        return this.transport.request(
            buildRequestConfig({ action, pathParams, params, queryParams, options })
        );
    }
}

export { SDK, Errors };

export { Dremio } from './constants';
export * from './Query';
